/**
 * Normaliza HTML de políticas para persistência:
 * - garante blocos <p> (converte <div> soltos)
 * - embute margem/line-height nos <p>/<h*> (leitura fora do editor)
 * - unwrap de <span> com estilo de editor colado no parágrafo pai
 */
export function normalizePolicyHtml(html) {
  if (html == null || html === '') return '';
  if (typeof document === 'undefined') return String(html);

  const root = document.createElement('div');
  root.innerHTML = String(html);

  const isMediaNode = (el) =>
    !!el?.closest?.('.defeitos-vv-media-wrap') ||
    el?.classList?.contains('defeitos-vv-media-wrap') ||
    el?.classList?.contains('defeitos-vv-media-frame') ||
    el?.classList?.contains('defeitos-vv-media-shield') ||
    el?.classList?.contains('defeitos-vv-media-resize-handles');

  // div → p (exceto mídia)
  Array.from(root.querySelectorAll('div')).forEach((div) => {
    if (isMediaNode(div)) return;
    const p = document.createElement('p');
    while (div.firstChild) p.appendChild(div.firstChild);
    div.replaceWith(p);
  });

  // Texto solto na raiz → <p>
  Array.from(root.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE && String(node.textContent || '').trim()) {
      const p = document.createElement('p');
      p.textContent = node.textContent;
      node.replaceWith(p);
    }
  });

  // <p> com só <br>/<br> → separar em parágrafos
  Array.from(root.querySelectorAll('p')).forEach((p) => {
    if (isMediaNode(p)) return;
    const htmlInner = p.innerHTML || '';
    if (!/<br\s*\/?>\s*<br\s*\/?>/i.test(htmlInner)) return;
    const parts = htmlInner.split(/<br\s*\/?>\s*<br\s*\/?>/i);
    if (parts.length < 2) return;
    const frag = document.createDocumentFragment();
    parts.forEach((part) => {
      const next = document.createElement('p');
      next.innerHTML = part.trim() || '<br>';
      frag.appendChild(next);
    });
    p.replaceWith(frag);
  });

  const ensureStyle = (el, key, value) => {
    const current = (el.getAttribute('style') || '').trim();
    const re = new RegExp(`(^|;)\\s*${key}\\s*:`, 'i');
    if (re.test(current)) return;
    el.style.setProperty(key, value);
  };

  const unwrapEditorSpan = (p) => {
    const children = Array.from(p.childNodes).filter(
      (n) => !(n.nodeType === Node.TEXT_NODE && !String(n.textContent || '').trim())
    );
    if (children.length !== 1 || children[0].nodeType !== Node.ELEMENT_NODE) return;
    const span = children[0];
    if (span.tagName !== 'SPAN') return;

    const fontSize = (span.style?.fontSize || '').trim();
    const fontWeight = (span.style?.fontWeight || '').trim();
    const color = (span.style?.color || '').trim();

    if (fontSize) ensureStyle(p, 'font-size', fontSize);
    if (fontWeight && fontWeight !== '400' && fontWeight !== 'normal') {
      ensureStyle(p, 'font-weight', fontWeight);
    }
    if (color) ensureStyle(p, 'color', color);

    // Título colado (ex.: 15px) → espaçamento de seção
    const px = parseFloat(fontSize);
    if (!Number.isNaN(px) && px >= 14) {
      ensureStyle(p, 'margin', '20px 0 8px');
      ensureStyle(p, 'line-height', '1.4');
      if (!fontWeight || fontWeight === '400' || fontWeight === 'normal') {
        ensureStyle(p, 'font-weight', '500');
      }
      if (!color) ensureStyle(p, 'color', '#18181b');
    }

    while (span.firstChild) p.insertBefore(span.firstChild, span);
    span.remove();
  };

  root.querySelectorAll('p').forEach((p) => {
    if (isMediaNode(p)) return;
    unwrapEditorSpan(p);
    ensureStyle(p, 'margin', '0 0 14px');
    ensureStyle(p, 'line-height', '1.7');
    if (!(p.getAttribute('style') || '').match(/(^|;)\s*color\s*:/i)) {
      ensureStyle(p, 'color', '#52525b');
    }
  });

  root.querySelectorAll('h1,h2,h3,h4').forEach((h) => {
    const tag = h.tagName.toLowerCase();
    const map = {
      h1: { margin: '24px 0 4px', fontSize: '24px', lineHeight: '1.25' },
      h2: { margin: '22px 0 4px', fontSize: '20px', lineHeight: '1.3' },
      h3: { margin: '20px 0 2px', fontSize: '17px', lineHeight: '1.35' },
      h4: { margin: '20px 0 2px', fontSize: '15px', lineHeight: '1.4' },
    };
    const cfg = map[tag];
    if (!cfg) return;
    ensureStyle(h, 'margin', cfg.margin);
    ensureStyle(h, 'font-size', cfg.fontSize);
    ensureStyle(h, 'line-height', cfg.lineHeight);
    ensureStyle(h, 'font-weight', tag === 'h1' || tag === 'h2' ? '600' : '500');
    ensureStyle(h, 'color', '#18181b');
  });

  return root.innerHTML;
}
